import { useEffect, useRef } from "react";

/**
 * Magnetic cursor — the ring is *pulled* toward any [data-magnetic] element
 * within ~120px of the cursor, snapping to its center. This produces a real
 * magnetic feeling on hover targets instead of just a resize cue.
 *
 * Targets:
 *   - [data-magnetic]  : strong pull, ring locks to center
 *   - a, button, [data-cursor='hover'], inputs, textarea, [role='button']
 *                      : ring grows (legacy hover styling)
 *   - [data-cursor='view'] : ring becomes a large crimson disc
 */
export default function MagneticCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(hover: none)").matches) return;

    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return;

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let ringX = mouseX;
    let ringY = mouseY;
    let target = null; // currently magnetised element

    const onMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      dot.style.transform = `translate(${mouseX}px, ${mouseY}px) translate(-50%, -50%)`;

      // find a magnetic element under-or-near the cursor
      const under = document.elementFromPoint(mouseX, mouseY);
      const candidate = under?.closest?.("[data-magnetic]") || null;
      if (candidate !== target) {
        target = candidate;
        if (target) ring.classList.add("magnetic");
        else ring.classList.remove("magnetic");
      }
    };

    let animationId;
    const raf = () => {
      let tx = mouseX;
      let ty = mouseY;
      if (target) {
        const r = target.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const d = Math.hypot(mouseX - cx, mouseY - cy);
        if (d < 140) {
          // 80% pull toward target center when close, falling off with distance
          const k = 1 - Math.min(1, d / 140);
          tx = mouseX + (cx - mouseX) * (0.55 + 0.35 * k);
          ty = mouseY + (cy - mouseY) * (0.55 + 0.35 * k);
        }
      }
      ringX += (tx - ringX) * 0.22;
      ringY += (ty - ringY) * 0.22;
      ring.style.transform = `translate(${ringX}px, ${ringY}px) translate(-50%, -50%)`;
      animationId = requestAnimationFrame(raf);
    };
    animationId = requestAnimationFrame(raf);

    const onOver = (e) => {
      const t = e.target;
      if (!t) return;
      if (t.closest("a, button, [data-cursor='hover'], input, textarea, [role='button']")) {
        ring.classList.add("hover");
        dot.classList.add("hover");
      }
      if (t.closest("[data-cursor='view']")) {
        ring.classList.add("view");
        dot.classList.add("view");
      }
    };
    const onOut = (e) => {
      const t = e.target;
      if (!t) return;
      if (t.closest("a, button, [data-cursor='hover'], input, textarea, [role='button']")) {
        ring.classList.remove("hover");
        dot.classList.remove("hover");
      }
      if (t.closest("[data-cursor='view']")) {
        ring.classList.remove("view");
        dot.classList.remove("view");
      }
    };

    window.addEventListener("mousemove", onMove);
    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseout", onOut);
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
    };
  }, []);

  return (
    <>
      <div ref={ringRef} className="cursor-ring" data-testid="cursor-ring" />
      <div ref={dotRef} className="cursor-dot" data-testid="cursor-dot" />
    </>
  );
}
